import React from 'react';

const ProgressTracker = ({ responses = {}, totalQuestions = 16 }) => {
  const completionCount = Object.values(responses).filter(
    response => response && response.trim().length > 0
  ).length;
  
  const progressPercentage = Math.round((completionCount / totalQuestions) * 100);
  
  const getStatusMessage = () => {
    if (progressPercentage === 0) {
      return "🎯 Ready to explore your interests";
    } else if (progressPercentage < 25) {
      return "🚀 Just getting started - great first steps!";
    } else if (progressPercentage < 50) {
      return "📈 Making good progress - patterns emerging";
    } else if (progressPercentage < 75) {
      return "🔥 Excellent progress - insights forming";
    } else if (progressPercentage < 100) {
      return "⭐ Almost there - final insights awaited";
    } else {
      return "🎉 Discovery complete - ready for analysis!";
    }
  };

  const getRecommendation = () => {
    if (progressPercentage === 0) {
      return "Start with any question that catches your eye - there's no wrong order!";
    } else if (progressPercentage < 50) {
      return "Keep going! Each response helps us understand your unique interests.";
    } else if (progressPercentage < 100) {
      return "You're doing great! Consider completing a few more for better program matching.";
    } else {
      return "Perfect! Click 'Start Exploring' to see your personalized program suggestions.";
    }
  };

  const getInsightAreas = () => {
    const insights = [
      { key: 'flow-state', label: 'Flow State', icon: '⚡' },
      { key: 'values', label: 'Core Values', icon: '💎' },
      { key: 'natural-strengths', label: 'Natural Strengths', icon: '💪' },
      { key: 'motivation-drivers', label: 'What Motivates You', icon: '🎯' },
      { key: 'aspirational-identity', label: 'Role Models', icon: '🌟' },
      { key: 'unique-perspective', label: 'Unique Views', icon: '🔍' },
      { key: 'learning-style', label: 'Learning Style', icon: '🧠' },
      { key: 'legacy-values', label: 'Legacy Goals', icon: '🏆' }
    ];

    return insights.map(insight => ({
      ...insight,
      completed: responses[insight.key] && responses[insight.key].trim().length > 0
    }));
  };

  return (
    <div className="space-y-6">
      {/* Main Progress Card */}
      <div className="bg-gradient-to-r from-emerald-50 to-emerald-50 border border-emerald-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-emerald-800">Discovery Progress</h3>
          <div className="text-right">
            <div className="text-2xl font-bold text-emerald-700">{progressPercentage}%</div>
            <div className="text-sm text-emerald-600">{completionCount}/{totalQuestions}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="w-full bg-emerald-100 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-emerald-500 to-emerald-500 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Status Message */}
        <div className="text-center">
          <div className="text-emerald-700 font-medium mb-1">
            {getStatusMessage()}
          </div>
          <div className="text-sm text-emerald-600">
            {getRecommendation()}
          </div>
        </div>
      </div>

      {/* Insight Areas Grid */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <h4 className="font-semibold text-slate-900 mb-4">Insight Areas</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {getInsightAreas().map((area) => (
            <div 
              key={area.key}
              className={`p-3 rounded-lg border text-center transition-all ${
                area.completed 
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                  : 'bg-stone-50 border-stone-200 text-stone-500'
              }`}
            >
              <div className={`text-lg mb-1 ${area.completed ? '' : 'grayscale opacity-50'}`}>
                {area.icon}
              </div>
              <div className="text-xs font-medium">
                {area.label}
              </div>
              {area.completed && (
                <div className="text-xs text-emerald-600 mt-1">✓</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Milestones */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <h4 className="font-semibold text-slate-900 mb-4">Milestones</h4>
        <div className="space-y-3">
          {[
            { threshold: 25, label: 'First Quarter', description: 'Basic interests identified', icon: '🎯' },
            { threshold: 50, label: 'Halfway Point', description: 'Personality patterns emerging', icon: '📈' },
            { threshold: 75, label: 'Three Quarters', description: 'Clear preferences forming', icon: '⭐' },
            { threshold: 100, label: 'Complete', description: 'Ready for AI program matching', icon: '🎉' }
          ].map((milestone) => (
            <div 
              key={milestone.threshold}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                progressPercentage >= milestone.threshold
                  ? 'bg-emerald-50 border border-emerald-200'
                  : 'bg-stone-50 border border-stone-200'
              }`}
            >
              <div className={`text-lg ${progressPercentage >= milestone.threshold ? '' : 'grayscale opacity-50'}`}>
                {milestone.icon}
              </div>
              <div className="flex-1">
                <div className={`font-medium text-sm ${
                  progressPercentage >= milestone.threshold ? 'text-emerald-700' : 'text-stone-500'
                }`}>
                  {milestone.label}
                </div>
                <div className={`text-xs ${
                  progressPercentage >= milestone.threshold ? 'text-emerald-600' : 'text-stone-400'
                }`}>
                  {milestone.description}
                </div>
              </div>
              {progressPercentage >= milestone.threshold && (
                <div className="text-emerald-500 text-sm">✓</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressTracker;